import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, ArrowRight } from 'lucide-react';
import { track } from './track';

const LOGO = '/veluro-logo.png';

const MENUS = [
  { label: 'Features', to: '/features' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Compare', to: '/compare' },
  { label: 'Demo', to: '/demo' },
];

export const scrollToHomeAnchor = (navigate, sel) => {
  if (window.location.pathname !== '/') {
    navigate('/');
    let tries = 0;
    const iv = setInterval(() => {
      const el = document.querySelector(sel);
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); clearInterval(iv); }
      else if (++tries > 20) clearInterval(iv);
    }, 150);
  } else {
    document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth' });
  }
};

export const HomeNav = () => {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const go = (item) => {
    setOpenMenu(null); setMobileOpen(false);
    track('feature_link_clicked', { label: item.label });
    if (item.to) navigate(item.to);
    else if (item.anchor) scrollToHomeAnchor(navigate, item.anchor);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/[0.06] bg-[#07070b]/85 backdrop-blur-xl">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
        <button data-testid="nav-logo" onClick={() => { if (window.location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' }); else navigate('/'); }} className="flex items-center">
          <img src={LOGO} alt="Veluro" className="h-12 scale-[1.55] origin-left object-contain drop-shadow-[0_0_18px_rgba(139,92,246,0.45)]" />
        </button>

        <div className="hidden lg:flex items-center gap-1" onMouseLeave={() => setOpenMenu(null)}>
          {MENUS.map((m) => (
            <div key={m.label} className="relative">
              <button data-testid={`nav-${m.label.toLowerCase().replace(/\s/g, '-')}`}
                onMouseEnter={() => setOpenMenu(m.items ? m.label : null)}
                onClick={() => (m.items ? setOpenMenu(openMenu === m.label ? null : m.label) : go(m))}
                className="flex items-center gap-1 px-3.5 py-2 text-[14.5px] text-white/80 hover:text-white font-bold transition-colors">
                {m.label} {m.items && <ChevronDown className="h-3.5 w-3.5 opacity-60" />}
              </button>
              {m.items && openMenu === m.label && (
                <div className="absolute top-full left-0 mt-1 w-60 rounded-xl bg-[#0d0d13] border border-white/10 shadow-2xl py-2">
                  {m.items.map((it) => (
                    <button key={it.label} onClick={() => go(it)}
                      className="block w-full text-left px-4 py-2.5 text-[13.5px] text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors">
                      {it.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <button data-testid="nav-login" onClick={() => navigate('/login')}
            className="hidden sm:block px-4 py-2 text-[14px] text-white/65 hover:text-white font-semibold transition-colors">
            Log In
          </button>
          <button data-testid="nav-start-trial" onClick={() => { track('nav_trial_clicked'); navigate('/pricing'); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:brightness-110 text-white font-semibold px-5 py-2.5 text-[14px] shadow-lg shadow-violet-500/25 transition-[filter]">
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen((v) => !v)} className="lg:hidden text-white/70 p-1.5">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/[0.06] bg-[#07070b] px-5 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
          {MENUS.map((m) => (
            <div key={m.label}>
              {m.items ? (
                <>
                  <p className="px-2 pt-3 pb-1 text-[11px] uppercase tracking-widest text-white/35 font-semibold">{m.label}</p>
                  {m.items.map((it) => (
                    <button key={it.label} onClick={() => go(it)} className="block w-full text-left px-2 py-2 text-[15px] text-white/75">{it.label}</button>
                  ))}
                </>
              ) : (
                <button onClick={() => go(m)} className="block w-full text-left px-2 py-2.5 text-[15px] text-white/85 font-medium">{m.label}</button>
              )}
            </div>
          ))}
          <button onClick={() => { setMobileOpen(false); navigate('/login'); }} className="block w-full text-left px-2 py-2.5 text-[15px] text-white/85 font-medium">Log In</button>
        </div>
      )}
    </header>
  );
};
